import { categories, partnerCount, type Partner } from '../data/integrations'
import { useT } from '../i18n/useT'
import type { Translation } from '../i18n/translations'

function PartnerCard({ p, t }: { p: Partner; t: Translation }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 flex flex-col hover:shadow-[0_16px_40px_-16px_rgb(15_23_42/0.15)] transition-shadow">
      <div className="h-12 flex items-center mb-5">
        <img src={p.logo} alt={`${p.name} logo`} className="max-h-10 w-auto object-contain" />
      </div>
      <h3 className="text-[17px] font-bold mb-2.5">{p.name}</h3>
      <ul className="space-y-1.5 mb-5 flex-1">
        {p.capabilities.map((c) => (
          <li key={c} className="flex items-start gap-2 text-sm text-ink-mute leading-snug">
            <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.6} className="w-3.5 h-3.5 mt-0.5 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {t.integrations.capabilities[c] ?? c}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
        <a
          href={p.website}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-ink-soft hover:text-brand-700 transition-colors"
        >
          {t.integrations.website}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M9 7h8v8" />
          </svg>
        </a>
        <a
          href={p.guide}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors"
        >
          {t.integrations.guide}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      </div>
    </div>
  )
}

export default function Integrations() {
  const t = useT()

  return (
    <>
      <section className="hero">
        <div className="mx-auto max-w-7xl px-8 pt-20 pb-16 text-center">
          <h1 className="text-5xl font-extrabold mb-5 max-w-3xl mx-auto leading-[1.1]">
            {t.integrations.h}
          </h1>
          <p className="text-lg text-ink-mute max-w-2xl mx-auto">
            {t.integrations.sub(partnerCount)}
          </p>
        </div>
      </section>

      {categories.map((cat, i) => (
        <section key={cat.name} className={i % 2 === 0 ? 'bg-white' : 'bg-mist'}>
          <div className="mx-auto max-w-7xl px-8 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-3xl font-extrabold mb-1.5">{t.integrations.cats[i].name}</h2>
                <p className="text-[15px] text-ink-mute">{t.integrations.cats[i].blurb}</p>
              </div>
              <span className="rounded-full bg-brand-100 text-brand-800 text-sm font-semibold px-4 py-1.5 shrink-0">
                {t.integrations.apps(cat.partners.length)}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-5">
              {cat.partners.map((p) => (
                <PartnerCard key={`${cat.name}-${p.name}`} p={p} t={t} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="bg-gradient-to-br from-brand-700 to-brand-900 text-white">
        <div className="mx-auto max-w-7xl px-8 py-16 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">
            {t.integrations.cta.h}
          </h2>
          <p className="text-brand-100 max-w-none mx-auto mb-8 whitespace-nowrap">
            {t.integrations.cta.sub}
          </p>
          <a
            href="mailto:info@simplepay.ca"
            className="inline-block rounded-md bg-white text-brand-800 font-semibold px-7 py-3.5 hover:bg-brand-50 transition-colors"
          >
            {t.integrations.cta.button}
          </a>
        </div>
      </section>
    </>
  )
}
